import { ArchBaseService } from "@/common/api.service";

/**
 * AI Workspace chat surface.
 *
 * Every endpoint here is gated on `ai.chat.use` server-side (ai-chat.controller.ts).
 * Hiding the menu is not access control — the frontend gate is a courtesy, not a
 * boundary, and these calls are expected to 403 for users without the permission.
 *
 * `ArchBaseService.init()` is called before every request rather than once at module
 * load. Every service in `common/api.service.js` writes to the same shared
 * `axios.defaults.baseURL` — `Vue.axios` and `axios` are one instance — so whichever
 * `init()` ran most recently owns the base URL globally. Initialising once here means
 * any later `ApiService.init()` or `StandappService.init()` silently redirects these
 * calls to stand-app.com. Hence the repetition, which matches every other service in
 * this repo.
 */
export const AiWorkspaceService = {
  /**
   * Skills offered in the composer picker.
   *
   * Published, non-archived Skills only. Returns Skills rather than Skill Versions —
   * clients never name a version, the backend resolves the live one at send time.
   *
   * @returns {Promise} [{ id, slug, name, description, isDefault }]
   */
  getSkills() {
    ArchBaseService.init();
    return ArchBaseService.get("ai/skills");
  },

  /**
   * The requesting user's conversations, most recently active first.
   *
   * `label` is always populated — either the stored title or one derived from the
   * Skill and creation date, so the history rail is never a column of blank rows.
   *
   * @returns {Promise} [{ id, title, label, messageCount, lastActivityAt, createdAt }]
   */
  getConversations() {
    ArchBaseService.init();
    return ArchBaseService.get("ai/conversations");
  },

  /**
   * Full history for one conversation, in `seq` order.
   *
   * Blocked messages arrive with `blockReason` and no `text` — they were never
   * stored anywhere. Messages still generating arrive with `status` of `pending` or
   * `streaming` and no `text`, because the answer is written once on completion.
   *
   * @param {String} conversationId
   * @returns {Promise} [{ id, seq, role, status, text?, blockReason?, skillVersionId, modelId, createdAt }]
   */
  getHistory(conversationId) {
    ArchBaseService.init();
    return ArchBaseService.get(`ai/conversations/${conversationId}/messages`);
  },

  /**
   * Send a message into an existing conversation and receive the finished answer.
   *
   * Non-streaming: this resolves with the complete answer. When ENG-2246 lands this
   * becomes Server-Sent Events, which changes the transport but not the persistence
   * either side of it. That conversion cannot reuse axios — SSE needs `fetch()` with
   * a ReadableStream, because `EventSource` supports neither a Bearer header nor POST.
   *
   * @param {String} conversationId
   * @param {String} message - raw user text
   * @param {String} skillId - the Skill governing this message, not a version
   * @returns {Promise} { userMessage, assistantMessage, contextTruncated }
   */
  sendMessage(conversationId, message, skillId) {
    ArchBaseService.init();
    return ArchBaseService.post(`ai/conversations/${conversationId}/messages`, {
      message,
      skillId,
    });
  },

  /**
   * Start a conversation with its first message.
   *
   * Conversation ids are backend-owned and a conversation is created by the message
   * that justifies it, so clicking "New" costs nothing and no blank rows accumulate
   * in the history rail.
   *
   * TEMPORARY: until the create-carries-first-message change lands in arch_backend,
   * `POST ai/conversations` creates an empty conversation and ignores the body. We
   * detect that by the absence of an answer in the response and follow up with a
   * send. This self-heals the moment the backend change ships — no flag to remember
   * to remove, just this branch becoming unreachable.
   *
   * @param {String} message
   * @param {String} skillId
   * @returns {Promise} { conversationId, userMessage, assistantMessage, contextTruncated }
   */
  async startConversation(message, skillId) {
    const created = await this.createConversation(message, skillId);

    if (created.answered) {
      return { data: created.payload };
    }

    const sent = await this.sendMessage(created.conversationId, message, skillId);
    return {
      data: { ...sent.data, conversationId: created.conversationId },
    };
  },

  /**
   * Create the conversation the first message will go into.
   *
   * Split out because the streaming send needs the same first step: its endpoint is
   * `POST …/conversations/:id/messages/stream` and there is no id until this has run.
   * Both callers therefore share one place that knows the temporary shape described
   * above, rather than each carrying its own copy of the assumption.
   *
   * `answered` says which shape came back. False is today's backend, which creates an
   * empty conversation and ignores the message — so the caller sends it separately, by
   * whichever transport it prefers. True is the create-carries-first-message change: the
   * answer is already generated and complete, so there is nothing left to stream and the
   * payload is used as it stands.
   *
   * @param {String} message
   * @param {String} skillId
   * @returns {Promise<Object>} { conversationId, answered, payload }
   */
  async createConversation(message, skillId) {
    ArchBaseService.init();
    const created = await ArchBaseService.post("ai/conversations", {
      message,
      skillId,
    });

    // `conversationId`, not `id`: both shapes this endpoint can return name it that way
    // — `{ conversationId }` for an empty conversation, `SendMessageResult` when the
    // create carried the first message. Reading `id` here silently yielded undefined on
    // every send, so the caller never had an id to reuse and opened a fresh conversation
    // for each message — the assistant appearing to forget everything one turn later.
    const conversationId = created.data?.conversationId;

    return {
      conversationId,
      answered: Boolean(created.data?.assistantMessage),
      payload: { ...created.data, conversationId },
    };
  },

  /**
   * Rename a conversation.
   *
   * A title the user wrote is the one kind of message text that belongs in MySQL —
   * they chose it as a label, rather than it being extracted from something they may
   * not have realised was sensitive.
   *
   * @param {String} conversationId
   * @param {String} title
   * @returns {Promise} { updated }
   */
  renameConversation(conversationId, title) {
    ArchBaseService.init();
    return ArchBaseService.patch(`ai/conversations/${conversationId}`, { title });
  },

  /**
   * User-facing delete. The record is retained; it stops being listed.
   *
   * @param {String} conversationId
   * @returns {Promise} { hidden }
   */
  hideConversation(conversationId) {
    ArchBaseService.init();
    return ArchBaseService.destroy(`ai/conversations/${conversationId}`);
  },

  /**
   * Whether the workspace is accepting messages, and where this user stands.
   *
   * Two separate facts, deliberately not collapsed into one (ADR-0007):
   *
   * - `enabled` — the two kill switches, workspace-wide. `ai_config.chat_enabled` and
   *   the `AI_CHAT_ENABLED` env var, folded server-side with either-off-means-off.
   * - `quota.configured` — whether *this user's role* has a quota profile. A role with
   *   none is denied, and the tempting shortcut of reporting that as `enabled: false`
   *   would hide the workspace and leave the admin who just granted `ai.chat.use`
   *   staring at a silent failure with no screen explaining it.
   *
   * `quota` is absent on a backend that has not shipped enforcement yet. Absent means
   * "no answer", which is not the same as `configured: false` and must not be treated
   * as a denial.
   *
   * Failures resolve to `enabled: true` rather than throwing. This is a courtesy
   * gate — sending is refused server-side with a message worth reading — and failing
   * closed here would hide the workspace from everyone on one transient 500.
   *
   * @returns {Promise<Object>} { enabled, quota? }
   */
  async getWorkspaceStatus() {
    // Deduplicated rather than cached: the sidebar and the workspace both ask on the
    // same load and there is no reason to ask twice, but caching the answer would blunt
    // a kill switch whose entire purpose is to take effect within seconds.
    if (!statusRequest) {
      ArchBaseService.init();
      statusRequest = ArchBaseService.get("ai/me")
        .then((response) => response.data || { enabled: true })
        .catch(() => {
          // Fail open: a status call that cannot be read must not lock the workspace.
          return { enabled: true };
        })
        .finally(() => {
          statusRequest = null;
        });
    }

    return statusRequest;
  },
};

/** The in-flight `GET ai/me`, shared by concurrent callers. Never a stored result. */
let statusRequest = null;

export default AiWorkspaceService;
